import { useState, useEffect, useCallback } from "react";
import { knowledgeApi } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/SimpleToast";
import { Trash2, ImageOff, Loader2 } from "lucide-react";

const abs = (url) => (url?.startsWith("http") ? url : `${process.env.REACT_APP_BACKEND_URL}${url}`);

export function ImageManager({ open, onOpenChange }) {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [orphans, setOrphans] = useState([]);
  const [total, setTotal] = useState(0);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await knowledgeApi.listOrphanImages();
      setOrphans(res.data.orphans || []);
      setTotal(res.data.total_images || 0);
    } catch { toast.error("Bilder konnten nicht geladen werden"); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const remove = async (id) => {
    setDeleting(id);
    try {
      await knowledgeApi.deleteImage(id);
      setOrphans((o) => o.filter((x) => x.id !== id));
      toast.success("Bild gelöscht");
    } catch { toast.error("Löschen fehlgeschlagen"); }
    finally { setDeleting(null); }
  };

  const removeAll = async () => {
    for (const o of [...orphans]) {
      try { await knowledgeApi.deleteImage(o.id); } catch { /* weiter */ }
    }
    toast.success("Verwaiste Bilder gelöscht");
    load();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="image-manager-dialog">
        <DialogHeader>
          <DialogTitle>Bild-Verwaltung</DialogTitle>
          <DialogDescription>
            Verwaiste Editor-Bilder (in keinem Artikel verwendet). Insgesamt {total} Bild(er), davon {orphans.length} verwaist.
          </DialogDescription>
        </DialogHeader>

        {orphans.length > 0 && (
          <div className="flex justify-end">
            <Button variant="destructive" size="sm" onClick={removeAll} data-testid="delete-all-orphans-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Alle verwaisten löschen
            </Button>
          </div>
        )}

        <div className="max-h-[55vh] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
          ) : orphans.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground" data-testid="no-orphans">
              <ImageOff className="w-12 h-12 mx-auto opacity-30" />
              <p className="mt-3 text-sm">Keine verwaisten Bilder – alles aufgeräumt.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {orphans.map((o) => (
                <div key={o.id} className="relative group border rounded-md overflow-hidden bg-muted/30" data-testid={`orphan-image-${o.id}`}>
                  <img src={abs(o.url)} alt="Verwaistes Bild" className="w-full h-28 object-contain" />
                  <Button
                    variant="destructive"
                    size="sm"
                    className="absolute top-1 right-1 h-7 w-7 p-0"
                    onClick={() => remove(o.id)}
                    disabled={deleting === o.id}
                    data-testid={`delete-orphan-${o.id}`}
                  >
                    {deleting === o.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash2 className="w-3.5 h-3.5" />}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
